import { useState, useEffect, useCallback, useMemo } from 'preact/hooks';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { formatRelative, formatEuros, formatDate } from '../lib/format.js';

// Waar 4U in één blik laat zien of er iets stilstaat.
//
// Why dit scherm bestaat: het aantal gebruikers per klant komt op een factuur.
// Een klant die al weken niets oplevert ziet er in een gewone lijst precies zo
// uit als een klant die netjes binnen zijn pakket past. Hier niet.

// De volgorde waarin problemen bovenaan komen. Wat aandacht nodig heeft eerst,
// wat in orde is onderaan.
const PRIORITEIT = { stale: 0, never: 1, no_users: 2, no_ref: 3, ok: 4 };

const TOELICHTING = {
  stale: 'De laatste ophaalpoging voor dit product is mislukt. Het getoonde aantal is ouder.',
  never: 'Nog nooit opgehaald. Er is niets bekend over deze klant.',
  no_users: 'Het product antwoordde, maar deze sleutel leverde geen gebruikers op.',
  no_ref: 'Er is geen sleutel ingevuld, dus er is niets geprobeerd.',
  ok: 'Opgehaald en geteld.'
};

const LABEL = {
  stale: 'verouderd',
  never: 'nooit opgehaald',
  no_users: 'geen gebruikers',
  no_ref: 'geen sleutel',
  ok: 'in orde'
};

const RUN_LABEL = {
  ok: 'gelukt',
  product_unreachable: 'product onbereikbaar',
  token_rejected: 'token geweigerd',
  product_token_missing: 'product kent zijn token niet'
};

function Toestand({ state }) {
  const klasse = state === 'ok' ? 'muted' : 'usage-over';
  return <span class={klasse} title={TOELICHTING[state]}>{LABEL[state]}</span>;
}

// Overgenomen pakketwissels en afdelingen, met wat er per al gefactureerde periode
// is bijgeboekt of te veel betaald. Apart, zodat de test het met echte data rendert
// (inloggen vraagt een TOTP-code).
export function PlanChangesCard({ changes, onAcknowledge }) {
  const nieuweWissels = changes.filter(c => c.acknowledgedAt == null);
  return (
    <Card title={nieuweWissels.length > 0
      ? `Pakketwissels en afdelingen (${nieuweWissels.length} nieuw)`
      : 'Pakketwissels en afdelingen'}>
      <p class="muted">
        Overgenomen uit het product. Een klant kan daar zelf naar een groter
        pakket overstappen of een afdeling toevoegen; 4U zet het abonnement dan
        om en boekt elke al gefactureerde periode vanaf de ingangsdatum bij: de
        eerste naar rato, de volgende helemaal. Een kleiner pakket wordt niet
        terugbetaald; wat de klant te veel betaalde staat erbij, zodat je zelf
        kunt beslissen. Een afdeling eraf telt vanaf de volgende periode.
      </p>
      <table class="data-table">
        <thead>
          <tr>
            <th>Debiteur</th><th>Van</th><th>Naar</th>
            <th>Vanaf</th><th>Bijgeboekt</th><th /><th />
          </tr>
        </thead>
        <tbody>
          {changes.map(c => (
            <tr key={c.id}>
              <td>{c.debtorName}</td>
              <td class="muted">{changeFrom(c)}</td>
              <td>{changeTo(c)}</td>
              <td class="muted">{formatDate(c.effectiveOn)}</td>
              {/* Een streepje betekent: niets bijgeboekt. Bij een downgrade,
                  of omdat de periode nog niet was gefactureerd. */}
              <td class="muted">
                {c.proratedAmountCents == null ? '—' : formatEuros(c.proratedAmountCents)}
                {/* Meer dan één periode bijgeboekt: per periode wat het werd */}
                {(c.periods ?? []).filter(p => p.chargedCents > 0).length > 1 && (
                  <div class="text-small">
                    {c.periods.filter(p => p.chargedCents > 0).map(p => (
                      <div key={p.periodStart}>{periodLabel(p.periodStart, p.periodEnd)}: {formatEuros(p.chargedCents)}</div>
                    ))}
                  </div>
                )}
                {c.overpaidCents != null && (
                  <div class="usage-over" title="Niet teruggeboekt. Beslis zelf of je dit crediteert.">
                    {formatEuros(c.overpaidCents)} te veel betaald
                    {(c.periods ?? []).filter(p => p.overpaidCents > 0).map(p => (
                      <div key={p.periodStart} class="text-small muted">{periodLabel(p.periodStart, p.periodEnd)}: {formatEuros(p.overpaidCents)}</div>
                    ))}
                  </div>
                )}
              </td>
              <td>
                {c.acknowledgedAt
                  ? <span class="muted">gezien</span>
                  : <span class="usage-over">nieuw</span>}
              </td>
              <td class="row-actions">
                {!c.acknowledgedAt && (
                  <Button variant="ghost" size="sm" onClick={() => onAcknowledge(c)}>Afvinken</Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export default function SyncStatusView() {
  const [links, setLinks] = useState([]);
  const [runs, setRuns] = useState([]);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [l, r, c] = await Promise.all([
        fetch('/api/sync-history/links', { credentials: 'same-origin' }),
        fetch('/api/sync-history/runs?limit=15', { credentials: 'same-origin' }),
        fetch('/api/plan-changes?limit=25', { credentials: 'same-origin' })
      ]);
      if (!l.ok || !r.ok || !c.ok) throw new Error('load_failed');
      setLinks((await l.json()).links || []);
      setRuns((await r.json()).runs || []);
      setChanges((await c.json()).changes || []);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const gesorteerd = useMemo(
    () => [...links].sort((a, b) =>
      (PRIORITEIT[a.state] - PRIORITEIT[b.state]) || a.debtorName.localeCompare(b.debtorName)),
    [links]
  );
  const aandacht = useMemo(() => links.filter(l => l.state !== 'ok'), [links]);

  async function afvinken(change) {
    const res = await fetch(`/api/plan-changes/${change.id}/acknowledge`, {
      method: 'POST', credentials: 'same-origin'
    });
    if (res.ok) refresh();
  }

  if (loading) return <Card><div class="empty-state muted">Ophaalstatus laden…</div></Card>;
  if (failed) return <Card><div class="empty-state muted">De ophaalstatus kon niet worden geladen.</div></Card>;

  return (
    <>
      {aandacht.length > 0 && (
        <Card title={`Vraagt aandacht (${aandacht.length})`}>
          <p class="muted">
            Deze koppelingen leveren geen betrouwbaar aantal op. De facturatierun
            slaat de bijbehorende debiteur over in plaats van te factureren op een
            aantal dat niemand heeft opgehaald.
          </p>
        </Card>
      )}

      {changes.length > 0 && <PlanChangesCard changes={changes} onAcknowledge={afvinken} />}

      <Card title="Koppelingen">
        <div class="card-actions">
          <Button variant="ghost" size="sm" onClick={refresh}>Vernieuwen</Button>
        </div>
        {links.length === 0 ? (
          <div class="empty-state">
            <p>Er zijn nog geen koppelingen.</p>
            <p class="muted">Koppel een debiteur aan een product via het tabblad Debiteuren.</p>
          </div>
        ) : (
          <table class="data-table">
            <thead>
              <tr>
                <th>Debiteur</th><th>Product</th><th>Sleutel</th>
                <th>Gebruikers</th><th>Toestand</th><th>Laatst geteld</th>
              </tr>
            </thead>
            <tbody>
              {gesorteerd.map(l => (
                <tr key={l.debtorProductId}>
                  <td>{l.debtorName}</td>
                  <td class="muted">{l.productName}</td>
                  <td>
                    {l.accountRef
                      ? <code>{l.accountRef}</code>
                      : <span class="muted">nog niet bekend</span>}
                  </td>
                  <td>
                    {/* Why: bij een verouderde of ontbrekende telling geen getal
                        dat als actueel leest. Dat is precies hoe een factuur op
                        oude cijfers ontstaat. */}
                    {l.userCount == null
                      ? <span class="muted">—</span>
                      : l.state === 'stale'
                        ? <span class="usage-over" title="Verouderd aantal">{l.userCount} ?</span>
                        : l.userCount}
                  </td>
                  <td><Toestand state={l.state} /></td>
                  <td class="muted">
                    {l.lastSyncedAt ? formatRelative(l.lastSyncedAt) : 'nooit'}
                    {l.state === 'stale' && l.productError && (
                      <div class="small">Laatste poging mislukt: {l.productError}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Laatste ophaalpogingen">
        {runs.length === 0 ? (
          <div class="empty-state muted">Nog geen pogingen vastgelegd.</div>
        ) : (
          <table class="data-table">
            <thead>
              <tr><th>Wanneer</th><th>Product</th><th>Uitkomst</th><th>Gebruikers</th><th>Melding</th></tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id}>
                  <td class="muted" title={r.ranAt}>{formatRelative(r.ranAt)}</td>
                  <td>{r.productName}</td>
                  <td>
                    {r.outcome === 'ok'
                      ? <span class="muted">{RUN_LABEL.ok}</span>
                      : <span class="usage-over">{RUN_LABEL[r.outcome] || r.outcome}</span>}
                  </td>
                  {/* Bij een mislukte poging is er niets geteld. Een nul zou dat verhullen. */}
                  <td class="muted">{r.userCount == null ? '—' : r.userCount}</td>
                  <td class="muted small">{r.errorMessage || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

// Een periode kort: "1–31 okt 2026", of "31 jan – 27 feb 2026" over een maandgrens.
// Why uit de losse delen: Safari leest "2026-10-01" niet overal als datum.
function periodLabel(startIso, endIso) {
  const parts = iso => iso.split('-').map(Number);
  const [sy, sm, sd] = parts(startIso);
  const [ey, em, ed] = parts(endIso);
  const month = (y, m) => new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('nl-NL', { month: 'short', timeZone: 'UTC' }).replace('.', '');
  if (sy === ey && sm === em) return `${sd}–${ed} ${month(ey, em)} ${ey}`;
  if (sy === ey) return `${sd} ${month(sy, sm)} – ${ed} ${month(ey, em)} ${ey}`;
  return `${sd} ${month(sy, sm)} ${sy} – ${ed} ${month(ey, em)} ${ey}`;
}

// Een pakketwissel toont het pakket; een ander aantal afdelingen het aantal extra
// afdelingen bij hetzelfde pakket
function changeFrom(c) {
  return c.kind === 'departments' ? `${c.fromPlanName}, ${extraLabel(c.fromExtraDepartments)}` : c.fromPlanName;
}

function changeTo(c) {
  return c.kind === 'departments' ? `${c.toPlanName}, ${extraLabel(c.toExtraDepartments)}` : c.toPlanName;
}

function extraLabel(n) {
  return n === 1 ? '1 extra afdeling' : `${n} extra afdelingen`;
}
